import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bell, User, Menu, X } from 'lucide-react';
import baseurl from '../Baseurl/baseurl';
import { Avatar, ThemeToggle, InstallButton } from '../ui';

const Header = () => {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const location = useLocation();

  // Separate refs for desktop and mobile dropdowns
  const desktopDropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);

  const navItems = [
    { name: 'Home', href: '/home' },
    { name: 'Members', href: '/members' },
    { name: 'Categories', href: '/categories' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  useEffect(() => {
    // Load logged-in user from localStorage on mount
    const storedData = localStorage.getItem('memberData');
    if (storedData) {
      setMemberData(JSON.parse(storedData));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('memberData');
    setShowProfileDropdown(false);
    setMemberData(null);
    console.log('Logout clicked');
    window.location.href = "/login";
  };

  const toggleMobileMenu = () => setShowMobileMenu(!showMobileMenu);
  const toggleProfileDropdown = () => setShowProfileDropdown(!showProfileDropdown);

  // Close dropdown when clicking outside - improved version
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check both desktop and mobile dropdown refs
      const isOutsideDesktop = desktopDropdownRef.current && !desktopDropdownRef.current.contains(event.target);
      const isOutsideMobile = mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target);

      if (isOutsideDesktop && isOutsideMobile) {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProfileDropdown]);

  // Handle profile link click
  const handleProfileClick = () => {
    setShowProfileDropdown(false);
  };

  // Handle logout with proper cleanup
  const handleLogoutClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowProfileDropdown(false);
    handleLogout();
  };

  const profileSrc = memberData?.profile_image ? `${baseurl}/${memberData.profile_image}` : undefined;
  const profileName = memberData?.first_name || '';

  return (
    <header className="sticky top-0 z-40 border-b border-line/10 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12">
          {/* Logo */}
          <Link to="/home" className="kbc-control flex items-center gap-2 group">
            <span className="relative inline-flex h-7 w-7 items-center justify-center overflow-hidden rounded-md ring-1 ring-inset ring-line/15">
              <img
                src="/image.png"
                alt="profile"
                className="h-full w-full rounded-full object-cover"
              />
            </span>
            <span className="font-display text-base font-semibold tracking-tight text-ink">
              KBC <em className="kbc-serif">Directory</em>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`relative rounded-md px-2.5 py-1 text-sm font-medium transition-colors duration-150 ${
                    isActive ? 'text-ink' : 'text-text hover:bg-line/5 hover:text-ink'
                  }`}
                >
                  {item.name}
                  {isActive && (
                    <span className="absolute inset-x-2.5 -bottom-[13px] h-0.5 rounded-full bg-brand" aria-hidden="true"></span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Desktop Icons Section */}
          <div className="hidden md:flex items-center gap-1.5">
            {/* Renders nothing once installed, or where the browser cannot
                offer an install, so this is the way back for anyone who
                dismissed the sheet. */}
            <InstallButton />
            <ThemeToggle />

            <Link
              to='/notifications'
              aria-label="Notifications"
              className="kbc-control relative inline-flex h-8 w-8 items-center justify-center rounded-md text-text ring-1 ring-inset ring-line/15 transition-colors duration-150 hover:bg-line/5 hover:text-ink"
            >
              <Bell className="w-4 h-4" />
              <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-brand"></span>
            </Link>

            {/* Desktop Profile Dropdown */}
            <div className="relative" ref={desktopDropdownRef}>
              <button
                onClick={toggleProfileDropdown}
                aria-label="Open profile menu"
                className="rounded-md ring-1 ring-inset ring-line/15 transition-colors duration-150 hover:ring-line/30"
              >
                {profileSrc ? (
                  <Avatar src={profileSrc} name={profileName} size="sm" />
                ) : (
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-surface2 text-muted">
                    <User className="w-4 h-4" />
                  </span>
                )}
              </button>

              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-56 rounded-lg border border-line/12 bg-surface py-1 shadow-lift z-50 text-left">
                  <div className="px-4 py-3 border-b border-line/10 text-left">
                    <p className="text-sm font-semibold text-ink text-left">
                      {memberData?.first_name
                        ? `${memberData.first_name}`
                        : 'Guest User'}
                    </p>
                    <p className="text-xs text-muted text-left">
                      {memberData?.email || memberData?.contact_no || 'Not logged in'}
                    </p>
                  </div>
                  {memberData ? (
                    <>
                      <Link
                        to="/profile"
                        onClick={handleProfileClick}
                        className="block px-4 py-2 text-sm text-text hover:bg-primary/8 hover:text-ink transition-colors text-left"
                      >
                        Profile
                      </Link>
                      <button
                        onClick={handleLogoutClick}
                        className="w-full text-left px-4 py-2 text-sm text-danger hover:bg-danger/10 transition-colors"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      onClick={handleProfileClick}
                      className="block px-4 py-2 text-sm text-primary hover:bg-primary/8 transition-colors text-left"
                    >
                      Login
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Icons Section - Menu, Profile and Notifications */}
          <div className="md:hidden flex items-center space-x-2">
            <InstallButton />
            {/* The nav links are hidden below md and the bottom bar only carries
                four of them, so without this About/Contact were reachable only
                by scrolling to the page footer. */}
            <button
              onClick={toggleMobileMenu}
              aria-label={showMobileMenu ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={showMobileMenu}
              aria-controls="mobile-nav"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-text ring-1 ring-inset ring-line/15 transition-colors duration-150 hover:text-ink"
            >
              {showMobileMenu ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>

            <ThemeToggle />

            <Link
              to='/notifications'
              aria-label="Notifications"
              className="kbc-control relative inline-flex h-10 w-10 items-center justify-center rounded-full text-text"
            >
              <Bell className="w-4 h-4" />
              <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-brand"></span>
            </Link>

            {/* Mobile Profile Dropdown */}
            <div className="relative" ref={mobileDropdownRef}>
              <button
                onClick={toggleProfileDropdown}
                aria-label="Open profile menu"
                className="rounded-md ring-1 ring-inset ring-line/15 transition-colors duration-150 hover:ring-line/30"
              >
                {profileSrc ? (
                  <Avatar src={profileSrc} name={profileName} size="sm" />
                ) : (
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-surface2 text-muted">
                    <User className="w-4 h-4" />
                  </span>
                )}
              </button>

              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-56 rounded-lg border border-line/12 bg-surface py-1 shadow-lift z-50 text-left">
                  <div className="px-4 py-3 border-b border-line/10 text-left">
                    <p className="text-sm font-semibold text-ink text-left">
                      {memberData?.first_name
                        ? `${memberData.first_name}`
                        : 'Guest User'}
                    </p>
                    <p className="text-xs text-muted text-left">
                      {memberData?.email || memberData?.contact_no || 'Not logged in'}
                    </p>
                  </div>

                  {/* Navigation Links in Mobile Dropdown */}
                  <div className="border-b border-line/10">
                    {navItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setShowProfileDropdown(false)}
                        className={`block px-4 py-2 text-sm transition-colors text-left ${
                          location.pathname === item.href
                            ? 'text-primary bg-primary/10 font-medium'
                            : 'text-text hover:bg-primary/8 hover:text-ink'
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>

                  {/* Auth Section */}
                  {memberData ? (
                    <>
                      <Link
                        to="/profile"
                        onClick={() => setShowProfileDropdown(false)}
                        className="block px-4 py-2 text-sm text-text hover:bg-primary/8 hover:text-ink transition-colors text-left"
                      >
                        Profile
                      </Link>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowProfileDropdown(false);
                          handleLogout();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-danger hover:bg-danger/10 transition-colors"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setShowProfileDropdown(false)}
                      className="block px-4 py-2 text-sm text-primary hover:bg-primary/8 transition-colors text-left"
                    >
                      Login
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile navigation panel */}
        {showMobileMenu && (
          <nav
            id="mobile-nav"
            className="md:hidden border-t border-line/10 py-1.5"
          >
            {navItems.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setShowMobileMenu(false)}
                  className={`block rounded-md px-3 py-3 text-sm font-medium transition-colors duration-150 ${
                    isActive ? 'bg-primary/10 text-ink' : 'text-text hover:bg-line/5 hover:text-ink'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
